import { Injectable } from '@angular/core';
import { flatMap, forkJoin, mergeMap, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ICall, ILinkedEntityRecord } from '../components/notetaker-card/notetaker-result/ICallInterface';
import { BullhornDataService } from './bullhorn-data.service';
import { NotetakerCallsService } from './notetaker-calls.service';

export interface ILinkedEntity {
  id: string;
  name: string;
  namespace: string;
  entityType: string;
}

export interface IAddLinkedEntityRequest {
  entityType: string;
  entityId: string;
  namespace: string;
}

@Injectable({
  providedIn: 'root'
})
export class LinkedEntitiesService {

  private static getEntityIdAndTypeFromLinkedEntityRecord(linkedEntityRecordSortKey: string): {entityId: string, type: string} {
    const parts = linkedEntityRecordSortKey.split('-');
    const entityType = parts[1];
    const entityId = parts[2];

    return {
      entityId: entityId,
      type: entityType,
    };
  }

  constructor(
    private notetakerCallsService: NotetakerCallsService,
    private bullhornDataService: BullhornDataService,
  ) {}

  // create method takes current call id and converts it to linked entity guid
  deleteLinkedEntity(corpId: string, entityType: string, entityId: string, currentCallId: string): Observable<void> {
    const guidParts = currentCallId.split('-');
    const meetingGuid = guidParts.slice(3, 8).join('-');
    const actualEntityId = entityId;

    return this.notetakerCallsService.getLinkedEntityRecord(corpId, meetingGuid).pipe(
      mergeMap((linkedEntityRecord) => {
        // error handling, create observable that emits undefined
        if (!linkedEntityRecord) {
          // todo check void 0
          return of();
        }

        const callIdToDelete = linkedEntityRecord.LinkedEntities.find((callId: string) => {
          return callId.includes(`Call-${entityType}-${actualEntityId}`);
        });

        if (!callIdToDelete) {
          return of();
        }

        const callToDelete = { corpId, callId: callIdToDelete } as ICall;
        return this.notetakerCallsService.deleteCall(callToDelete).pipe(
          mergeMap(() => {
            // created variable for further usage below
            const remainingEntities = linkedEntityRecord.LinkedEntities.filter(
              (callId: string) => callId !== callIdToDelete
            );
            // handle cleanup
            // if no entities left, delete the linked entity record
            // otherwise update linkedentity record
            if (remainingEntities.length === 0) {
              return this.notetakerCallsService.deleteCall({ corpId, callId: linkedEntityRecord.sortKey } as ICall);
            } else {
              linkedEntityRecord.LinkedEntities = remainingEntities;
              return this.notetakerCallsService.putLinkedEntityRecord(linkedEntityRecord);
            }
          })
        );
      })
    );
  }

  addLinkedEntity(
    corpId: string,
    linkedEntityType: string,
    linkedEntityId: string,
    currentCallId: string,
    namespace: string,
    originalCall?: ICall
  ): Observable<string> {
    return of(null).pipe(
      switchMap(() => {

        const parts = currentCallId.split('-');
        const linkedEntityGuid = parts.slice(3, 8).join('-');
        const newUUID = crypto.randomUUID();
        const newCallId = `Call-${linkedEntityType}-${linkedEntityId}-${linkedEntityGuid}-${newUUID}`;

        const callInfoObj = { linkedEntities: [{ namespace, path: [linkedEntityType, linkedEntityId] }] };

        const rawJson = JSON.stringify(callInfoObj);
        const escaped = rawJson
          .replace(/\\/g, '\\\\')
          .replace(/"/g, '\\"');

        const newCall = {
          corpId,
          callId: newCallId,
          entityId: linkedEntityId,
          entityType: linkedEntityType,
          callInfo: escaped,
          date: originalCall?.date || new Date().toISOString(),
          status: originalCall?.status,
          description: originalCall?.description,
          duration: originalCall?.duration,
          callerId: originalCall?.callerId,
          attendeeEmail: originalCall?.attendeeEmail,
          summaryPath: originalCall?.summaryPath,
          transcriptPath: originalCall?.transcriptPath
        } as any;

        return this.notetakerCallsService.getLinkedEntityRecord(corpId, linkedEntityGuid).pipe(
          mergeMap((linkEntityRecord) => {
            if (!linkEntityRecord) {
              linkEntityRecord = {
                corpId: corpId,
                sortKey: `LinkedEntity-${linkedEntityGuid}`,
                LinkedEntities: [newCallId],
              };
            } else {
              let linkedEntitiesStr = typeof linkEntityRecord.LinkedEntities === 'string'
                ? linkEntityRecord.LinkedEntities
                : JSON.stringify(linkEntityRecord.LinkedEntities);
              linkedEntitiesStr = linkedEntitiesStr.replace(/&quot;/g, '"');
              const linkedEntities = JSON.parse(linkedEntitiesStr);
              linkedEntities.push(newCallId);
              linkEntityRecord.LinkedEntities = linkedEntities;
            }
            return this.notetakerCallsService.putLinkedEntityRecord(linkEntityRecord);
          }),
          mergeMap(linkEntityRecord => {
            return this.notetakerCallsService.saveCall(newCall);
          }), map(() => newCallId));
      })
    );
  }

  private getFriendlyIdFieldForEntityType(entityType: string): string[] {
    switch (entityType) {
      case 'Candidate':
      case 'ClientContact':
      case 'ClientCorporation':
      case 'Lead':
        return ['id', 'name'];
      case 'JobOrder':
      case 'Opportunity':
        return ['id', 'title'];
      case 'Placement':
        return ['id', 'jobOrder(title)', 'candidate(name)'];
    }
  }

  private mapEntityResponseToEntityTitle(entity: any, entityType: string): string {
    switch (entityType) {
      case 'Candidate':
      case 'ClientContact':
      case 'ClientCorporation':
      case 'Lead':
        return entity.name;
      case 'JobOrder':
      case 'Opportunity':
        return entity.title;
      case 'Placement':
        return `${entity.candidate.name} - ${entity.jobOrder.title}`;
    }
  }

  // remove all entities
  clearAllLinkedEntities(corpId: string, currentCallId: string): Observable<void> {
    const meetingGuid = currentCallId.split('-').slice(3, 8).join('-');

    // get linked entity record
    return this.notetakerCallsService.getLinkedEntityRecord(corpId, meetingGuid).pipe(
      mergeMap((linkedEntityRecord) => {
        // log error
        if (!linkedEntityRecord) {
          return of(void 0);
        }

        // gets linked entity id's, filer out current call (dont delete it)
        // map each call to delete
        // delete change name
        const deleteObservables = linkedEntityRecord.LinkedEntities
          .filter((callId: string) => callId !== currentCallId)
          .map((callId: string) => this.notetakerCallsService.deleteCall({ corpId, callId } as ICall));

        if (deleteObservables.length === 0) {
          return this.notetakerCallsService.deleteCall({ corpId, callId: linkedEntityRecord.sortKey } as ICall);
        }

        return forkJoin(deleteObservables).pipe(
          mergeMap(() => this.notetakerCallsService.deleteCall({ corpId, callId: linkedEntityRecord.sortKey } as ICall))
        );
      })
    );
  }

  // todo this can probably be changed - just an endpoint needed to get the linked entities record
  // reduces the need to manipulate strings also and easier to update the entities then
  getLinkedEntitiesForCall(corpId: string, callId: string): Observable<ILinkedEntity[]> {
    return this.notetakerCallsService.getLinkedEntityRecord(corpId, callId.split('-').slice(3, 8).join('-'))
      .pipe(mergeMap(linkedEntityRecord => {
      if (!linkedEntityRecord) {
        return of([]);
      }

      let linkedEntitiesStr = typeof linkedEntityRecord.LinkedEntities === 'string'
        ? linkedEntityRecord.LinkedEntities
        : JSON.stringify(linkedEntityRecord.LinkedEntities);
      linkedEntitiesStr = linkedEntitiesStr.replace(/&quot;/g, '"');
      const linkedEntities = JSON.parse(linkedEntitiesStr);

      const filteredEntities = linkedEntities;

      const entityIdMap: Record<string, string[]> = filteredEntities
        .map(LinkedEntitiesService.getEntityIdAndTypeFromLinkedEntityRecord)
        .reduce((acc, entity) => {
          if (acc[entity.type] instanceof Array) {
            acc[entity.type].push(entity.entityId);
          } else {
            acc[entity.type] = [entity.entityId];
          }
          return acc;
        }, {});
      const calls: Observable<ILinkedEntity[]>[] = Object.entries(entityIdMap).map(([entityType, ids]) => {

        return this.bullhornDataService.getActiveEntities(
          entityType,
          ids,
          this.getFriendlyIdFieldForEntityType(entityType),
        ).pipe(
            map(results => results.map(result => ({
              id: result.id,
              name: this.mapEntityResponseToEntityTitle(result, entityType),
              namespace: 'bullhorn', // Set appropriate namespace
              entityType: entityType
            } as ILinkedEntity)))
          );
        });
      if (calls.length === 0) {
        return of([]);
      }

      return forkJoin(calls).pipe(map(results => results.flat()));
    }));
  }
}
